import getDecryptedToken from "../../../../api/getDecryptedToken";
import { setEncryptedToken } from "../../../../api/encriptToken";
import {
  addMinutesToHm,
  formatTimeHm,
  minutesBetweenTimes,
} from "../components/appointmentScheduleUi";

export const CLINIC_TIME_DURATION_CHANGED_EVENT = "clinicTimeDurationChanged";

/** Default slot length and step for new appointments (minutes). */
export const DEFAULT_APPOINTMENT_TIME_GAP = 10;

/** @deprecated Use DEFAULT_APPOINTMENT_TIME_GAP or getStoredAppointmentTimeGap() */
export const NEW_APPOINTMENT_INTERVAL_MINUTES = DEFAULT_APPOINTMENT_TIME_GAP;

export const APPOINTMENT_TIME_GAP_OPTIONS = [5, 10, 15] as const;
export type AppointmentTimeGapMinutes = (typeof APPOINTMENT_TIME_GAP_OPTIONS)[number];

/** Default length of one calendar-selected appointment slot (minutes).
 * Matches the calendar's half-hour markers (`calendar-half-hour`). */
export const NEW_APPOINTMENT_SLOT_MINUTES = 30;

/** Matches calendar click granularity (`CusomizeCalender` STEP). */
export const NEW_APPOINTMENT_CALENDAR_STEP_MINUTES = 5;

/**
 * Turn a react-big-calendar slot click/drag into start/end.
 * A single 5-min click becomes a 30-min appointment; a drag keeps the selected range.
 */
export function rangeFromCalendarSlotInfo(
  slot: { start?: Date | string; end?: Date | string; slots?: Array<Date | string> },
  options?: { stepMinutes?: number; defaultMinutes?: number }
): { start: Date; end: Date } {
  const step = options?.stepMinutes ?? NEW_APPOINTMENT_CALENDAR_STEP_MINUTES;
  const defaultMinutes = options?.defaultMinutes ?? NEW_APPOINTMENT_SLOT_MINUTES;
  const start = new Date(slot.start ?? slot.slots?.[0] ?? Date.now());
  const rawEnd = slot.end != null ? new Date(slot.end) : null;
  const selectedMinutes =
    rawEnd && !Number.isNaN(rawEnd.getTime())
      ? Math.round((rawEnd.getTime() - start.getTime()) / 60000)
      : 0;

  const end = new Date(start);
  if (rawEnd && selectedMinutes > step) {
    end.setTime(rawEnd.getTime());
  } else {
    end.setMinutes(end.getMinutes() + defaultMinutes);
  }
  return { start, end };
}

/**
 * HTML `type="time"` step for the end-time field.
 * Must match calendar clicks (every 5 min), not clinic time gap — otherwise
 * e.g. 10:05 + 15 min → 10:20 is invalid when gap is 15 (quarter-hour grid only).
 */
export const NEW_APPOINTMENT_TIME_INPUT_STEP_SECONDS =
  NEW_APPOINTMENT_CALENDAR_STEP_MINUTES * 60;

export function isValidAppointmentTimeGap(
  n: number
): n is AppointmentTimeGapMinutes {
  return n === 5 || n === 10 || n === 15;
}

export function parseAppointmentTimeGap(
  value: unknown
): AppointmentTimeGapMinutes {
  const n = parseInt(String(value ?? ""), 10);
  return isValidAppointmentTimeGap(n) ? n : DEFAULT_APPOINTMENT_TIME_GAP;
}

/** Reads clinic `timeDuration` saved in the encrypted login session. */
export function getStoredAppointmentTimeGap(): AppointmentTimeGapMinutes {
  return parseAppointmentTimeGap(getDecryptedToken("timeDuration"));
}

/** Updates encrypted session after clinical settings save (no re-login required). */
export function setStoredAppointmentTimeGap(minutes: number): void {
  if (!isValidAppointmentTimeGap(minutes)) return;
  setEncryptedToken("timeDuration", String(minutes));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(CLINIC_TIME_DURATION_CHANGED_EVENT, {
        detail: minutes,
      })
    );
  }
}

export function getNewAppointmentDurationOptions(
  gapMinutes?: number
): number[] {
  const gap = isValidAppointmentTimeGap(gapMinutes ?? NaN)
    ? gapMinutes!
    : getStoredAppointmentTimeGap();
  const options = [1, 2, 3, 4, 5, 6].map((n) => n * gap);
  const set = new Set(options);
  set.add(NEW_APPOINTMENT_SLOT_MINUTES);
  return Array.from(set).sort((a, b) => a - b);
}

/** @deprecated Use getNewAppointmentDurationOptions(gap) */
export const NEW_APPOINTMENT_DURATION_OPTIONS = getNewAppointmentDurationOptions(
  DEFAULT_APPOINTMENT_TIME_GAP
);

/** Snap a duration in minutes to the nearest step (minimum one step). */
export function snapNewAppointmentDuration(
  minutes: number,
  step?: number
): number {
  const gap = isValidAppointmentTimeGap(step ?? NaN)
    ? step!
    : getStoredAppointmentTimeGap();
  if (!Number.isFinite(minutes) || minutes <= 0) return NEW_APPOINTMENT_SLOT_MINUTES;
  if (minutes === NEW_APPOINTMENT_SLOT_MINUTES) return NEW_APPOINTMENT_SLOT_MINUTES;
  return Math.max(gap, Math.round(minutes / gap) * gap);
}

export function parseQueueNumber(raw: string): number | null {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function formatAppointmentDateYmd(value: unknown): string | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local Date from appointment date + start time (for matching doctor hours). */
export function buildLocalDateTimeFromFields(
  dateValue: unknown,
  startTime: unknown
): Date | null {
  const raw = String(dateValue ?? "").trim();
  const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const ymd = ymdMatch
    ? `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`
    : formatAppointmentDateYmd(dateValue);
  const hm = formatTimeHm(String(startTime ?? ""));
  if (!ymd || !hm) return null;
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const [hh, mm] = hm.split(":").map((n) => parseInt(n, 10));
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Max early/late minutes around a doctor session for Walk-in / Other. */
export const SESSION_EDGE_GRACE_MINUTES = 30;

type DoctorHourSlot = {
  day?: number;
  sessionName?: string | null;
  roomNumber?: string | number | null;
  start?: { hours?: number; minutes?: number };
  end?: { hours?: number; minutes?: number };
};

type SessionBlock = {
  sessionName: string;
  startM: number;
  endM: number;
  roomNumber: string;
};

function clockToMinutes(hours?: number, minutes?: number): number {
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
}

function uniqueRoomFromValues(values: string[]): string {
  const seen: string[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i].trim();
    if (!v || seen.indexOf(v) !== -1) continue;
    seen.push(v);
  }
  return seen.length === 1 ? seen[0] : "";
}

/** Merge half-hour rows into one block per session name for that weekday. */
export function mergeDoctorSessionBlocks(
  timeSlots: DoctorHourSlot[] | undefined,
  day: number
): SessionBlock[] {
  const byName = new Map<
    string,
    { startM: number; endM: number; rooms: string[] }
  >();

  for (const interval of timeSlots ?? []) {
    if (interval?.day !== day) continue;
    const sessionName = String(interval?.sessionName ?? "").trim();
    if (!sessionName) continue;
    const startM = clockToMinutes(interval?.start?.hours, interval?.start?.minutes);
    const endM = clockToMinutes(interval?.end?.hours, interval?.end?.minutes);
    if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) {
      continue;
    }
    const room = String(interval?.roomNumber ?? "").trim();
    const existing = byName.get(sessionName);
    if (!existing) {
      byName.set(sessionName, {
        startM,
        endM,
        rooms: room ? [room] : [],
      });
    } else {
      existing.startM = Math.min(existing.startM, startM);
      existing.endM = Math.max(existing.endM, endM);
      if (room) existing.rooms.push(room);
    }
  }

  const merged: SessionBlock[] = [];
  byName.forEach((block, sessionName) => {
    merged.push({
      sessionName,
      startM: block.startM,
      endM: block.endM,
      roomNumber: uniqueRoomFromValues(block.rooms),
    });
  });
  merged.sort((a, b) => a.startM - b.startM || a.endM - b.endM);
  return merged;
}

/**
 * Expand each session by up to ±grace minutes.
 * Gaps between sessions are split evenly (capped at grace), so close sessions
 * do not both claim the same early/late window.
 */
export function sessionBlocksWithEdgeGrace(
  blocks: SessionBlock[],
  graceMinutes: number = SESSION_EDGE_GRACE_MINUTES
): Array<SessionBlock & { effectiveStartM: number; effectiveEndM: number }> {
  const grace = Math.max(0, graceMinutes);
  const n = blocks.length;
  return blocks.map((block, i) => {
    const gapBefore =
      i === 0 ? Number.POSITIVE_INFINITY : block.startM - blocks[i - 1].endM;
    const gapAfter =
      i === n - 1 ? Number.POSITIVE_INFINITY : blocks[i + 1].startM - block.endM;

    const graceBefore =
      i === 0
        ? grace
        : gapBefore <= 0
          ? 0
          : Math.min(grace, Math.floor(gapBefore / 2));
    const graceAfter =
      i === n - 1
        ? grace
        : gapAfter <= 0
          ? 0
          : Math.min(grace, Math.floor(gapAfter / 2));

    return {
      ...block,
      effectiveStartM: block.startM - graceBefore,
      effectiveEndM: block.endM + graceAfter,
    };
  });
}

/**
 * Match a local datetime to dentist hours.
 * Core session time wins; otherwise ±grace at start/end (gap split when close).
 */
export function slotMetaForDoctorHours(
  timeSlots: any[] | undefined,
  slotStart: Date | null,
  graceMinutes: number = SESSION_EDGE_GRACE_MINUTES
): { sessionName: string; roomNumber: string; matched: boolean } {
  if (!slotStart || Number.isNaN(slotStart.getTime())) {
    return { sessionName: "", roomNumber: "", matched: false };
  }

  const day = slotStart.getDay();
  const startM = slotStart.getHours() * 60 + slotStart.getMinutes();
  const blocks = mergeDoctorSessionBlocks(timeSlots, day);
  if (!blocks.length) {
    return { sessionName: "", roomNumber: "", matched: false };
  }

  const coreHit = blocks.find((b) => startM >= b.startM && startM < b.endM);
  if (coreHit) {
    return {
      sessionName: coreHit.sessionName,
      roomNumber: coreHit.roomNumber,
      matched: true,
    };
  }

  const withGrace = sessionBlocksWithEdgeGrace(blocks, graceMinutes);
  const graceHits = withGrace.filter(
    (b) => startM >= b.effectiveStartM && startM < b.effectiveEndM
  );
  if (graceHits.length !== 1) {
    return { sessionName: "", roomNumber: "", matched: false };
  }

  return {
    sessionName: graceHits[0].sessionName,
    roomNumber: graceHits[0].roomNumber,
    matched: true,
  };
}

export function collectUsedSessionQueues(
  appointments: any[],
  opts: { date: string; doctorId: string; sessionName: string }
): Set<number> {
  const used = new Set<number>();
  const sessionKey = String(opts.sessionName ?? "").trim();
  const doctorKey = String(opts.doctorId ?? "").trim();
  if (!sessionKey || !doctorKey || !opts.date) return used;

  for (const apt of appointments) {
    if (!apt) continue;
    if (String(apt.sessionName ?? "").trim() !== sessionKey) continue;
    const aptDoctor = String(apt.doctorId?._id ?? apt.doctorId ?? "").trim();
    if (aptDoctor !== doctorKey) continue;
    const aptDate = formatAppointmentDateYmd(apt.date);
    if (aptDate !== opts.date) continue;
    const q = parseQueueNumber(String(apt.queue ?? ""));
    // 0 is the default / unassigned queue — not a reserved slot in the session.
    if (q !== null && q !== 0) used.add(q);
  }
  return used;
}

function timeFromSlotDetail(detail: any, key: "start" | "end"): string {
  const asTime = formatTimeHm(detail?.[`${key}Time`]);
  if (asTime) return asTime;
  if (key === "start") {
    return formatTimeHm(detail?.start ?? detail?.slots?.[0]);
  }
  if (detail?.end) return formatTimeHm(detail.end);
  const slots = detail?.slots;
  if (Array.isArray(slots) && slots.length > 1) {
    const lastStart = new Date(slots[slots.length - 1]);
    if (!Number.isNaN(lastStart.getTime())) {
      lastStart.setMinutes(
        lastStart.getMinutes() + NEW_APPOINTMENT_CALENDAR_STEP_MINUTES
      );
      return formatTimeHm(lastStart);
    }
  }
  return "";
}

export function buildNewAppointmentFormState(detail: any) {
  const startTime = timeFromSlotDetail(detail, "start");
  const endTime = timeFromSlotDetail(detail, "end");
  const fromSlot = minutesBetweenTimes(startTime, endTime);
  const interval =
    Number.isFinite(fromSlot) && fromSlot > 0
      ? fromSlot
      : NEW_APPOINTMENT_SLOT_MINUTES;
  const resolvedEnd =
    endTime || (startTime ? addMinutesToHm(startTime, interval) : "");
  const date =
    formatAppointmentDateYmd(detail?.date) ??
    formatAppointmentDateYmd(detail?.start) ??
    formatAppointmentDateYmd(detail?.slots?.[0]) ??
    "";

  return {
    title: "",
    fullName: "",
    gender: "",
    mobileNo: "",
    dob: "",
    address: "",
    doctorId: detail?.doctorId,
    date,
    startTime,
    endTime: resolvedEnd,
    interval: String(interval),
    purpose: [] as string[],
    activeTime: "",
    patientId: "",
    queue: "0",
    caseNumber: "",
    sessionName: detail?.sessionName,
    walkIn: Boolean(detail?.walkIn),
    other: Boolean(detail?.other ?? detail?.callIn),
    patientTypeLabel: "" as string,
    registerdDate: undefined as string | Date | undefined,
    createdAt: undefined as string | Date | undefined,
    regNo: "" as string,
  };
}

export type NewAppointmentFormState = ReturnType<
  typeof buildNewAppointmentFormState
>;

function toResourceId(value: any): string | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") {
    const nested = value._id ?? value.id;
    if (nested != null && nested !== "") return String(nested);
    return undefined;
  }
  const s = String(value).trim();
  return !s || s === "[object Object]" ? undefined : s;
}

function overlapResourceKey(event: any): string {
  return (
    toResourceId(event?.resourceId) ||
    toResourceId(event?.data?.appointment?.doctorId) ||
    "unassigned"
  );
}

const OFF_CALENDAR_TAGS = new Set(["walk_in", "walkin", "other", "call_in", "callin"]);

/** Walk-in and Other never occupy a doctor slot (legacy status walkin/callin too). */
function isOffCalendarAppointment(event: any): boolean {
  const appt = event?.data?.appointment;
  if (!appt) return false;
  return [
    appt.pre_appointment_status,
    appt.preAppointmentStatus,
    appt.booking_source,
    appt.bookingSource,
    appt.status,
  ].some((v) =>
    OFF_CALENDAR_TAGS.has(
      String(v ?? "")
        .trim()
        .toLowerCase()
        .replace(/-/g, "_")
    )
  );
}

function isInactiveForSlotOverlap(event: any): boolean {
  if (event?.data?.blockout) return true;
  const apt = event?.data?.appointment;
  if (!apt) return true;
  const pas = String(
    apt.pre_appointment_status ?? apt.preAppointmentStatus ?? ""
  )
    .trim()
    .toLowerCase();
  if (pas === "cancel" || pas === "cancelled" || pas === "canceled") {
    return true;
  }
  const st = String(apt.status ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  return (
    st === "cancelled" ||
    st === "canceled" ||
    st === "none" ||
    st === "cancel" ||
    st === "tray"
  );
}

/** Touching end===start is allowed; overlapping ranges are not. */
export function dateRangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

type EventOverlapParams = {
  start: Date;
  end: Date;
  doctorId?: unknown;
  excludeAppointmentId?: string;
};

function findOverlappingEvent(
  events: any[] | undefined,
  params: EventOverlapParams,
  blocks: (event: any) => boolean
): any | null {
  if (!events?.length || !params.start || !params.end) return null;
  const doctorKey = toResourceId(params.doctorId);
  const exclude = String(params.excludeAppointmentId ?? "").trim();

  for (const event of events) {
    if (!event?.data?.appointment) continue;
    if (!blocks(event)) continue;
    if (isInactiveForSlotOverlap(event)) continue;

    const evId = String(
      event?.data?.appointment?.id ?? event?.data?.appointment?._id ?? ""
    ).trim();
    if (exclude && evId && evId === exclude) continue;

    const evDoctor = overlapResourceKey(event);
    if (doctorKey && evDoctor !== "unassigned" && evDoctor !== doctorKey) continue;

    const evStart = event.start instanceof Date ? event.start : new Date(event.start);
    const evEnd = event.end instanceof Date ? event.end : new Date(event.end);
    if (dateRangesOverlap(params.start, params.end, evStart, evEnd)) {
      return event;
    }
  }
  return null;
}

export function findOverlappingAppointmentEvent(
  events: any[] | undefined,
  params: EventOverlapParams
): any | null {
  return findOverlappingEvent(
    events,
    params,
    (event) => !isOffCalendarAppointment(event)
  );
}

const OTHER_TAGS = new Set(["other", "call_in", "callin"]);

/** Other (legacy call_in) appointment, not cancelled / no-show. */
function isActiveOtherAppointment(event: any): boolean {
  const appt = event?.data?.appointment;
  if (!appt) return false;
  const st = String(appt.status ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (st === "no_show" || st === "noshow") return false;
  return [
    appt.pre_appointment_status,
    appt.preAppointmentStatus,
    appt.booking_source,
    appt.bookingSource,
    appt.status,
  ].some((v) =>
    OTHER_TAGS.has(
      String(v ?? "")
        .trim()
        .toLowerCase()
        .replace(/-/g, "_")
    )
  );
}

/**
 * Other bookings only clash with other active Other bookings for the same
 * doctor/day. Booked, online and walk-in rows are ignored.
 */
export function findOverlappingOtherEvent(
  events: any[] | undefined,
  params: EventOverlapParams
): any | null {
  return findOverlappingEvent(events, params, isActiveOtherAppointment);
}

export function otherOverlapMessage(event: any): string {
  const startDate = event?.start instanceof Date ? event.start : new Date(event?.start);
  const endDate = event?.end instanceof Date ? event.end : new Date(event?.end);
  const start = Number.isNaN(startDate.getTime()) ? "" : formatTimeHm(startDate);
  const end = Number.isNaN(endDate.getTime()) ? "" : formatTimeHm(endDate);
  const appt = event?.data?.appointment;
  const name = String(
    appt?.patientName ?? appt?.patientId?.fullName ?? ""
  ).trim();
  const range = start && end ? `${start}–${end}` : "";
  const detail = [name, range].filter(Boolean).join(", ");
  return `This time overlaps another Other appointment for this doctor${
    detail ? ` (${detail})` : ""
  }. Choose a free time.`;
}

export function occupiedSlotMessage(event: any): string {
  const startDate = event?.start instanceof Date ? event.start : new Date(event?.start);
  const endDate = event?.end instanceof Date ? event.end : new Date(event?.end);
  const start = Number.isNaN(startDate.getTime()) ? "" : formatTimeHm(startDate);
  const end = Number.isNaN(endDate.getTime()) ? "" : formatTimeHm(endDate);
  if (start && end) {
    return `This time overlaps another appointment (${start}–${end}). Choose a free slot.`;
  }
  return "This time slot is already booked. Choose a free slot.";
}
